import { ResponsiveContainer, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { short } from "@/components/LiveIntelTable";
import { fmtMins, fmtX, PERIODS, CADENCES } from "@/lib/liveProfile";

const AMBER = "#FBBF24";
const CYAN = "#00F0FF";
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const tick = (t, cad) => { const [, m, d] = t.split("-").map(Number); return cad === "monthly" ? MON[m - 1] : `${d} ${MON[m - 1]}`; };

export const METRICS = {
  lives: { label: "Total lives", val: (s) => s.lives, fmt: (v) => (v == null ? "—" : Math.round(v).toLocaleString()) },
  likes: { label: "Typical likes per live", val: (s) => s.likes, fmt: (v) => (v == null ? "—" : Math.round(v).toLocaleString()), likes: true },
  eng: { label: "Engagement rate", val: (s) => s.eng, fmt: (v) => (v == null ? "—" : `${Number(v).toFixed(2)}%`), likes: true },
  mins: { label: "Avg live length", val: (s) => s.mins, fmt: fmtMins },
};

const lbl = "mono text-[10px] uppercase tracking-[0.25em] text-zinc-500";

function Btn({ on, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} aria-pressed={on}
      className={`mono text-xs px-3 py-1.5 rounded-md border transition-colors duration-150 ${on ? "text-cyan-300 border-cyan-400/60 bg-cyan-400/10" : "hairline text-zinc-400 hover:text-white hover:border-white/30"}`}>{children}</button>
  );
}

function CrowdBox({ title, big, sub, accent, children, testid }) {
  return (
    <div data-testid={testid} className="border hairline rounded-md p-5 min-w-0" style={{ background: "linear-gradient(180deg, rgba(0,240,255,0.05), rgba(0,240,255,0.01))" }}>
      <div className={lbl}>{title}</div>
      <div className="display text-3xl font-semibold text-white mt-2" data-testid={`${testid}-value`}>{big}</div>
      {children}
      {sub && <div className="mono text-[11px] text-zinc-400 mt-3">{sub}</div>}
      {accent && <div className="mono text-[11px] mt-1.5" style={{ color: CYAN }} data-testid={`${testid}-accent`}>{accent}</div>}
    </div>
  );
}

function CrowdRow({ c }) {
  const v = c.viewers;
  const ok = (n) => n != null && v;
  const pct = ok(c.subs) && c.subs ? (v * 100) / c.subs : null;
  const scale = c.best || Math.max(v || 0, c.peak || 0) || 1;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="lp-crowd">
      <CrowdBox testid="lp-crowd-typical" title="Typical live viewers" big={v == null ? "—" : v.toLocaleString()}>
        <div className="relative h-1.5 rounded-full mt-4" style={{ background: "rgba(255,255,255,0.06)" }} data-testid="lp-crowd-bar">
          <div className="h-full rounded-full" style={{ width: `${Math.min(100, ((v || 0) * 100) / scale)}%`, background: CYAN }} />
          {c.peak != null && <div className="absolute -top-1 w-0.5 h-3.5 bg-white" style={{ left: `${Math.min(100, (c.peak * 100) / scale)}%` }} />}
        </div>
        <div className="mono text-[11px] text-zinc-400 mt-3" data-testid="lp-crowd-typical-line">typical · peak {c.peak == null ? "—" : c.peak.toLocaleString()} · best {c.best == null ? "—" : c.best.toLocaleString()}</div>
      </CrowdBox>
      <CrowdBox testid="lp-crowd-best" title="Best peak" big={c.best == null ? "—" : c.best.toLocaleString()} sub="biggest live crowd"
        accent={ok(c.best) ? `${fmtX(c.best / v)}× the typical crowd` : null} />
      <CrowdBox testid="lp-crowd-views1d" title="Views after 1 day" big={c.views_1d == null ? "—" : short(c.views_1d)} sub="typical per live stream"
        accent={ok(c.views_1d) ? `≈ ${fmtX(c.views_1d / v)}× the live crowd` : null} />
      <CrowdBox testid="lp-crowd-subs" title="Live crowd vs subscribers" big={pct == null ? "—" : `${pct.toFixed(2)}%`} sub="of subscribers watch live"
        accent={pct ? `1 in ${Math.round(c.subs / v).toLocaleString()} subscribers` : null} />
    </div>
  );
}

function Spark({ points, color }) {
  const vals = points.map((p) => p ?? 0);
  if (vals.length < 2) return <div className="h-10" />;
  const max = Math.max(...vals) || 1;
  const d = vals.map((y, i) => `${(i * 100) / (vals.length - 1)},${38 - (y / max) * 34}`).join(" ");
  return <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="w-full h-10 mt-3"><polyline points={d} fill="none" stroke={color} strokeWidth="1.6" vectorEffect="non-scaling-stroke" /></svg>;
}

function StreamBox({ k, p, series, on, onClick }) {
  const m = METRICS[k];
  const hidden = m.likes && p.hidden;
  return (
    <button type="button" onClick={onClick} data-testid={`lp-box-${k}`} aria-pressed={on}
      className={`text-left border rounded-md p-5 min-w-0 transition-[border-color,background-color] duration-150 ${on ? "border-amber-400/60" : "hairline hover:border-white/25"}`}
      style={{ background: on ? "rgba(251,191,36,0.06)" : "var(--surface-2)" }}>
      <div className="flex items-start justify-between gap-2">
        <span className={lbl}>{m.label}</span>
        <span className="mono text-[9px] px-1.5 py-0.5 rounded border" style={on ? { color: AMBER, borderColor: `${AMBER}99` } : { color: "#71717A", borderColor: "rgba(255,255,255,0.08)" }}>{on ? "On chart" : "Plot"}</span>
      </div>
      <div className="display text-3xl font-semibold text-white mt-2 truncate" data-testid={`lp-box-${k}-value`}>{hidden ? "Likes hidden" : m.fmt(m.val(p))}</div>
      <Spark points={series.map((s) => (hidden ? 0 : m.val(s)))} color={on ? AMBER : CYAN} />
    </button>
  );
}

function ChartTip({ active, payload, metric }) {
  if (!active || !payload?.length) return null;
  const s = payload[0].payload;
  return (
    <div className="border hairline rounded px-2.5 py-1.5 text-xs" style={{ background: "#121214" }} data-testid="lp-chart-tip">
      <div className="text-white">{METRICS[metric].fmt(s.v)}</div>
      <div className="text-zinc-400">{s.lives} live{s.lives === 1 ? "" : "s"}</div>
    </div>
  );
}

function Chart({ p, cadence, metric, type, period }) {
  const m = METRICS[metric];
  const series = p.series[cadence].map((s) => ({ ...s, v: m.val(s) ?? 0, x: tick(s.t, cadence) }));
  let empty = null;
  if (!p.count) empty = "No live streams on KOL Radar in this period";
  else if (m.likes && p.hidden) empty = "Likes hidden";
  if (empty) return <div className="h-[300px] flex items-center justify-center text-sm text-zinc-500" data-testid="lp-chart-empty">{empty}</div>;
  const axis = { stroke: "#52525B", fontSize: 11, tickLine: false, axisLine: false };
  const common = { data: series, margin: { top: 10, right: 8, left: -10, bottom: 0 } };
  const grid = <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />;
  const xa = <XAxis dataKey="x" {...axis} minTickGap={24} />;
  const ya = <YAxis {...axis} width={48} tickFormatter={(v) => (metric === "mins" ? fmtMins(v) : metric === "eng" ? `${v}%` : short(v))} />;
  const tip = <Tooltip cursor={{ fill: "rgba(255,255,255,0.03)" }} content={<ChartTip metric={metric} />} />;
  return (
    <div className="h-[300px]" data-testid="lp-chart" data-type={type} data-cadence={cadence} data-period={period} data-points={series.length}>
      <ResponsiveContainer>
        {type === "bar" ? (
          <BarChart {...common}>
            <defs><linearGradient id="lpBar" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={CYAN} /><stop offset="100%" stopColor={CYAN} stopOpacity={0.35} /></linearGradient></defs>
            {grid}{xa}{ya}{tip}
            <Bar dataKey="v" fill="url(#lpBar)" radius={[2, 2, 0, 0]} maxBarSize={64} isAnimationActive={false} />
          </BarChart>
        ) : (
          <AreaChart {...common}>
            <defs><linearGradient id="lpArea" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={CYAN} stopOpacity={0.4} /><stop offset="100%" stopColor={CYAN} stopOpacity={0} /></linearGradient></defs>
            {grid}{xa}{ya}{tip}
            <Area dataKey="v" type="monotone" stroke={CYAN} strokeWidth={2} fill="url(#lpArea)" isAnimationActive={false} />
          </AreaChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

export function LivePerformance({ c, live, ui, set }) {
  const p = live.periods[ui.period];
  const words = PERIODS.find((x) => x.k === ui.period).words;
  const cadWord = CADENCES.find((x) => x.k === ui.cadence).label;
  return (
    <section className="border hairline rounded-md p-6" style={{ background: "var(--surface)" }} data-testid="lp-performance">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mono text-[11px] uppercase tracking-[0.3em]" style={{ color: CYAN }}>Live performance</div>
          <p className="text-sm text-zinc-400 mt-1.5">How big the live crowd is, and how their live streams perform over time.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="lp-periods">
          <span className={`${lbl} mr-1`}>Period</span>
          {PERIODS.map((x) => <Btn key={x.k} on={ui.period === x.k} onClick={() => set.period(x.k)} testid={`lp-period-${x.k}`}>{x.label}</Btn>)}
        </div>
      </div>
      <div className="flex items-center gap-3 mt-6 mb-4"><span className="mono text-[11px] uppercase tracking-[0.25em] text-zinc-300">Live crowd</span><span className="flex-1 h-px bg-white/5" /></div>
      <CrowdRow c={c} />
      <div className="flex items-center gap-3 mt-8 mb-4">
        <span className="mono text-[11px] uppercase tracking-[0.25em] text-zinc-300">Live streams on KOL Radar</span>
        <span className="mono text-[11px] text-zinc-500" data-testid="lp-streams-period">{words}</span>
        <span className="flex-1 h-px bg-white/5" />
        <span className="hidden sm:inline mono text-[11px] text-zinc-500">Click a box to chart it</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="lp-boxes">
        {Object.keys(METRICS).map((k) => <StreamBox key={k} k={k} p={p} series={p.series[ui.cadence]} on={ui.metric === k} onClick={() => set.metric(k)} />)}
      </div>
      <div className="border hairline rounded-md p-5 mt-4" style={{ background: "var(--surface-2)" }}>
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <div className={lbl} data-testid="lp-chart-sub">{cadWord} · {words}</div>
            <div className="display text-xl font-semibold mt-1" data-testid="lp-chart-title">{METRICS[ui.metric].label}</div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {CADENCES.map((x) => <Btn key={x.k} on={ui.cadence === x.k} onClick={() => set.cadence(x.k)} testid={`lp-cad-${x.k}`}>{x.label}</Btn>)}
            <span className="w-2" />
            <Btn on={ui.type === "bar"} onClick={() => set.type("bar")} testid="lp-type-bar">Bars</Btn>
            <Btn on={ui.type === "area"} onClick={() => set.type("area")} testid="lp-type-area">Area</Btn>
          </div>
        </div>
        <Chart p={p} cadence={ui.cadence} metric={ui.metric} type={ui.type} period={ui.period} />
      </div>
    </section>
  );
}
